import React, { useState, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import ClassSectionFilter from './parts/ClassSectionFilter';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
// ↑ adjust path if your store lives elsewhere

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

interface FileEntry {
  id: string;
  file: File;
  rollNumber: string;
  preview: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  errorMsg?: string;
  resultUrl?: string;
}

interface UploadSummary {
  total: number;
  success: number;
  failed: number;
}

// ─── Photo Status Types (matches new API) ─────────────────────────────────────

interface PhotoStatusStudent {
  user_id: string;
  name: string;
  roll_no: number | string;
  profile_picture_url?: string;
}

interface SectionStat {
  section: string | null;
  total_students: number;
  with_photo_count: number;
  without_photo_count: number;
  summary: string;
  with_photo: PhotoStatusStudent[];
  without_photo: PhotoStatusStudent[];
}

interface PhotoStatusResponse {
  status: string;
  grand_total: number;
  grand_with_photo: number;
  grand_without_photo: number;
  grand_summary: string;
  sections: SectionStat[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'gif'];
const MAX_SIZE_KB = 500;
const MAX_SIZE_B = MAX_SIZE_KB * 1024;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function parseEntry(file: File): FileEntry {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const rollNumber = file.name.replace(/\.[^.]+$/, '');
  const preview = URL.createObjectURL(file);
  const id = uid();

  if (!ALLOWED_EXTS.includes(ext))
    return {
      id,
      file,
      rollNumber,
      preview,
      status: 'error',
      errorMsg: `Invalid type (.${ext}). Use JPG, PNG or GIF.`,
    };
  if (file.size > MAX_SIZE_B)
    return {
      id,
      file,
      rollNumber,
      preview,
      status: 'error',
      errorMsg: `Exceeds 500 KB (${(file.size / 1024).toFixed(0)} KB).`,
    };
  if (!/^\d+$/.test(rollNumber))
    return {
      id,
      file,
      rollNumber,
      preview,
      status: 'error',
      errorMsg: `Filename must be roll number only (e.g. 42.jpg).`,
    };

  return { id, file, rollNumber, preview, status: 'pending' };
}

function normalizeUserId(id: unknown): string {
  if (id == null) return '';
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id !== null && '$oid' in (id as object)) {
    return String((id as { $oid: string }).$oid);
  }
  return String(id);
}

// ─── Main Component ───────────────────────────────────────────────────────────

const BulkProfilePictureUpload: React.FC = () => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  // ── Session from school details store ──────────────────────────────────────
  const schoolDetails = useSchoolDetailsStore((s) => s.schoolDetails);
  const sessionId =
    schoolDetails?.current_session?.id ??
    schoolDetails?.base_session?.id ??
    null;

  // ── Tab state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'upload' | 'status'>('upload');

  // ── Upload state ───────────────────────────────────────────────────────────
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [showAllSections, setShowAllSections] = useState(false);
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [summary, setSummary] = useState<UploadSummary | null>(null);

  // ── Photo status state ─────────────────────────────────────────────────────
  const [photoStatus, setPhotoStatus] = useState<PhotoStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusClassId, setStatusClassId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  // ── Derived (upload) ───────────────────────────────────────────────────────
  const pendingEntries = entries.filter((e) => e.status === 'pending');
  const errorEntries = entries.filter((e) => e.status === 'error');
  const successEntries = entries.filter((e) => e.status === 'success');
  const canUpload =
    pendingEntries.length > 0 && !!selectedClass && !!sessionId && !uploading;

  const selectedClassData = classes.find((c) => c.id === selectedClass);
  const hasSections = (selectedClassData?.sections?.length ?? 0) > 0;
  const sectionRequired = hasSections && !selectedSection && !showAllSections;

  const getClassName = (classId: string): string => {
    const found = classes.find((c) => c.id === classId);
    return found ? found.class_name : classId;
  };

  // ── File handling ──────────────────────────────────────────────────────────

  const addFiles = useCallback((files: FileList | File[]) => {
    const parsed = Array.from(files).map(parseEntry);
    setEntries((prev) => {
      const map = new Map(prev.map((e) => [e.id, e]));
      parsed.forEach((e) => {
        const existing = prev.find(
          (p) => p.rollNumber === e.rollNumber && p.status !== 'success'
        );
        if (existing) {
          URL.revokeObjectURL(existing.preview);
          map.delete(existing.id);
        }
        map.set(e.id, e);
      });
      return Array.from(map.values());
    });
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeEntry = (id: string) => {
    setEntries((prev) => {
      const entry = prev.find((e) => e.id === id);
      if (entry) URL.revokeObjectURL(entry.preview);
      return prev.filter((e) => e.id !== id);
    });
  };

  const clearAll = () => {
    entries.forEach((e) => URL.revokeObjectURL(e.preview));
    setEntries([]);
    setSummary(null);
  };

  // ── Upload (new API) ───────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!selectedClass || !sessionId || pendingEntries.length === 0) return;

    setUploading(true);
    setSummary(null);

    setEntries((prev) =>
      prev.map((e) => (e.status === 'pending' ? { ...e, status: 'uploading' } : e))
    );

    const formData = new FormData();
    formData.append('class_id', selectedClass);
    formData.append('session_id', sessionId);
    if (selectedSection && !showAllSections) {
      formData.append('section', selectedSection);
    }
    // Backend expects form key "files" (not "files[]")
    pendingEntries.forEach((entry) => {
      formData.append('files', entry.file, entry.file.name);
    });

    try {
      const response = await apiClient.post(
        '/api/admin/profile-pictures/bulk',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const data = response.data;
      const resultMap = new Map<
        string,
        { success: boolean; error?: string; url?: string }
      >();

      if (Array.isArray(data.results)) {
        data.results.forEach((r: any) => {
          const roll =
            r.roll_number != null
              ? String(r.roll_number)
              : String(r.file ?? '').replace(/\.[^.]+$/, '');
          const ok = r.status === 'success';
          resultMap.set(roll, {
            success: ok,
            error: r.error,
            url: r.file_url,
          });
        });
      }

      setEntries((prev) =>
        prev.map((e) => {
          if (e.status !== 'uploading') return e;
          const res = resultMap.get(e.rollNumber);
          if (!res)
            return { ...e, status: 'error', errorMsg: 'No response from server' };
          return res.success
            ? { ...e, status: 'success', resultUrl: res.url }
            : { ...e, status: 'error', errorMsg: res.error ?? 'Upload failed' };
        })
      );

      const successCount = data.success_count ?? 0;
      const failCount = data.fail_count ?? 0;
      setSummary({
        total: successCount + failCount,
        success: successCount,
        failed: failCount,
      });

      if (failCount === 0) {
        toast.current?.show({
          severity: 'success',
          summary: 'All Done!',
          detail: `${successCount} photo${successCount !== 1 ? 's' : ''} uploaded.`,
          life: 5000,
        });
      } else if (successCount === 0) {
        toast.current?.show({
          severity: 'error',
          summary: 'Upload Failed',
          detail: `All ${failCount} files failed.`,
          life: 5000,
        });
      } else {
        toast.current?.show({
          severity: 'warn',
          summary: 'Partial Success',
          detail: `${successCount} uploaded, ${failCount} failed.`,
          life: 5000,
        });
      }
    } catch (err: any) {
      setEntries((prev) =>
        prev.map((e) =>
          e.status === 'uploading'
            ? {
                ...e,
                status: 'error',
                errorMsg: err.response?.data?.message ?? 'Network error',
              }
            : e
        )
      );
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message ?? 'Upload request failed',
        life: 5000,
      });
    } finally {
      setUploading(false);
    }
  };

  const retryFailed = () => {
    setEntries((prev) =>
      prev.map((e) =>
        e.status === 'error' && /^\d+$/.test(e.rollNumber)
          ? { ...e, status: 'pending', errorMsg: undefined }
          : e
      )
    );
    setSummary(null);
  };

  // ── Class / section handlers ───────────────────────────────────────────────

  const handleClassChange = (classId: string | null) => {
    setSelectedClass(classId);
    setSelectedSection(null);
    setShowAllSections(false);
  };

  const handleSectionChange = (section: string | null) => {
    setSelectedSection(section);
    setShowAllSections(false);
  };

  // ── Photo status handlers ──────────────────────────────────────────────────

  const fetchPhotoStatus = async (classId: string) => {
    if (!sessionId) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Session required',
        detail: 'Active academic session is required to load photo status.',
        life: 4000,
      });
      return;
    }

    setStatusLoading(true);
    setStatusClassId(classId);
    try {
      const response = await apiClient.get('/api/admin/profile-pictures/status', {
        params: {
          class_id: classId,
          session_id: sessionId,
        },
      });

      const data = response.data;
      const sections: SectionStat[] = (data.sections ?? []).map((sec: any) => ({
        ...sec,
        section:
          sec.section === 'NO_SECTION' || sec.section === ''
            ? null
            : sec.section,
        with_photo: (sec.with_photo ?? []).map((s: any) => ({
          ...s,
          user_id: normalizeUserId(s.user_id),
          roll_no: s.roll_no,
        })),
        without_photo: (sec.without_photo ?? []).map((s: any) => ({
          ...s,
          user_id: normalizeUserId(s.user_id),
          roll_no: s.roll_no,
        })),
      }));

      setPhotoStatus({
        status: data.status,
        grand_total: data.grand_total ?? 0,
        grand_with_photo: data.grand_with_photo ?? 0,
        grand_without_photo: data.grand_without_photo ?? 0,
        grand_summary: data.grand_summary ?? '',
        sections,
      });
      setExpandedSections(new Set());
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail:
          err.response?.data?.message ??
          err.response?.data?.error ??
          'Failed to fetch photo status',
        life: 4000,
      });
      setPhotoStatus(null);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleStatusTabClick = () => {
    setActiveTab('status');
    if (selectedClass && sessionId) {
      fetchPhotoStatus(selectedClass);
    } else if (classes.length > 0 && sessionId) {
      fetchPhotoStatus(classes[0].id);
    }
  };

  const toggleSectionExpand = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleDeletePhoto = async (userId: string, studentName: string) => {
    if (!userId) return;
    if (
      !window.confirm(
        `Remove profile picture for ${studentName}? This cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingUserId(userId);
    try {
      const response = await apiClient.post('/api/admin/profile-pictures/delete', {
        user_id: userId,
      });
      if (response.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: response.data.message ?? 'Profile picture removed',
          life: 3000,
        });
        if (statusClassId) fetchPhotoStatus(statusClassId);
      } else {
        throw new Error(response.data.message || 'Delete failed');
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message ?? err.message ?? 'Failed to delete',
        life: 4000,
      });
    } finally {
      setDeletingUserId(null);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .bpp-entry-row { animation: fadeSlideIn 0.25s ease-out; transition: background 0.15s; }
        .bpp-entry-row:hover { background: rgba(65,105,225,0.03) !important; }
        .bpp-drop-zone { transition: border-color 0.2s, background 0.2s, transform 0.15s; }
        .bpp-drop-zone.dragging { border-color: #4169E1 !important; background: rgba(65,105,225,0.06) !important; transform: scale(1.01); }
        .bpp-remove-btn { opacity: 0; transition: opacity 0.15s; }
        .bpp-entry-row:hover .bpp-remove-btn { opacity: 1; }
        .status-section-row { transition: background 0.15s; cursor: pointer; }
        .status-section-row:hover { background: rgba(65,105,225,0.02) !important; }

        @media (max-width: 600px) {
          .bpp-file-grid {
            grid-template-columns: 44px 1fr 32px !important;
          }
          .bpp-file-size-col,
          .bpp-file-status-col,
          .bpp-file-col-header-size,
          .bpp-file-col-header-status { display: none !important; }
        }

        .status-table-header,
        .status-section-grid {
          display: grid;
        }
        .status-table-header  { grid-template-columns: 1fr 80px 90px 80px 130px 32px; }
        .status-section-grid  { grid-template-columns: 1fr 80px 90px 80px 130px 32px; }

        @media (max-width: 640px) {
          .status-table-header { display: none !important; }
          .status-section-grid {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
          }
          .status-section-grid .scol-total,
          .status-section-grid .scol-with,
          .status-section-grid .scol-missing,
          .status-section-grid .scol-coverage,
          .status-section-grid .scol-chevron {
            display: none !important;
          }
          .status-section-title { width: 100% !important; }
          .status-section-mobile-stats { display: flex !important; }
        }

        .status-section-mobile-stats { display: none; flex-wrap: wrap; gap: 6px; margin-top: 4px; }

        .mobile-stat-pill {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 9px; border-radius: 20px;
          font-size: 11px; font-weight: 700;
        }
      `}</style>

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <GlobalHeaderTitle
          title="Bulk Profile Pictures"
          subtitle="Upload profile photos for multiple students at once"
          icon="pi pi-images"
          buttons={
            activeTab === 'upload'
              ? [
                  ...(entries.length > 0
                    ? [
                        {
                          label: 'Clear All',
                          icon: 'pi pi-trash',
                          onClick: clearAll,
                          outlined: true,
                          disabled: uploading,
                          style: {
                            borderWidth: '2px',
                            color: '#ef4444',
                            borderColor: '#fca5a5',
                          },
                        },
                      ]
                    : []),
                  {
                    label: uploading
                      ? 'Uploading…'
                      : `Upload ${pendingEntries.length > 0 ? `(${pendingEntries.length})` : ''}`,
                    icon: uploading ? 'pi pi-spin pi-spinner' : 'pi pi-cloud-upload',
                    onClick: handleUpload,
                    disabled: !canUpload || sectionRequired,
                  },
                ]
              : []
          }
        />

        {!sessionId && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: 12,
              padding: '10px 16px',
              marginBottom: 16,
              fontSize: 13,
              color: '#92400e',
              fontWeight: 600,
            }}
          >
            <i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b' }} />
            Active academic session not found. Ensure school details are loaded
            (current_session / base_session) before uploading or viewing status.
          </div>
        )}

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button
            onClick={() => setActiveTab('upload')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '9px 18px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 13,
              background:
                activeTab === 'upload'
                  ? 'linear-gradient(135deg, #4169E1, #5B7CE6)'
                  : 'white',
              color: activeTab === 'upload' ? 'white' : '#6b7280',
              boxShadow:
                activeTab === 'upload'
                  ? '0 4px 12px rgba(65,105,225,0.3)'
                  : '0 1px 4px rgba(0,0,0,0.08)',
            }}
          >
            <i className="pi pi-cloud-upload" style={{ fontSize: 13 }} />
            Upload Photos
          </button>

          <button
            onClick={handleStatusTabClick}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '9px 18px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 13,
              background:
                activeTab === 'status'
                  ? 'linear-gradient(135deg, #4169E1, #5B7CE6)'
                  : 'white',
              color: activeTab === 'status' ? 'white' : '#6b7280',
              boxShadow:
                activeTab === 'status'
                  ? '0 4px 12px rgba(65,105,225,0.3)'
                  : '0 1px 4px rgba(0,0,0,0.08)',
            }}
          >
            <i className="pi pi-chart-bar" style={{ fontSize: 13 }} />
            Photo Status
          </button>
        </div>

        {/* ══════════════════ UPLOAD TAB ══════════════════ */}
        {activeTab === 'upload' && (
          <>
            <div
              style={{
                background: 'linear-gradient(135deg, #eff6ff, #f0f9ff)',
                border: '1px solid #bfdbfe',
                borderRadius: 16,
                padding: '14px 20px',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  flexShrink: 0,
                  background: 'linear-gradient(135deg, #4169E1, #5B7CE6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-info-circle" style={{ color: 'white', fontSize: 16 }} />
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: '#1e40af',
                    marginBottom: 4,
                  }}
                >
                  How to use bulk upload
                </div>
                <div style={{ fontSize: 12, color: '#3b82f6', lineHeight: 1.7 }}>
                  1. Select the <strong>class</strong> (and section if applicable)
                  below.&nbsp; 2. Name each image file after the student&apos;s{' '}
                  <strong>roll number</strong> — e.g.{' '}
                  <code
                    style={{
                      background: '#dbeafe',
                      padding: '1px 5px',
                      borderRadius: 4,
                    }}
                  >
                    42.jpg
                  </code>
                  ,{' '}
                  <code
                    style={{
                      background: '#dbeafe',
                      padding: '1px 5px',
                      borderRadius: 4,
                    }}
                  >
                    7.png
                  </code>
                  .&nbsp; 3. Drop or select all files, then click <strong>Upload</strong>.
                </div>
              </div>
            </div>

            <ClassSectionFilter
              selectedClass={selectedClass}
              selectedSection={selectedSection}
              onClassChange={handleClassChange}
              onSectionChange={handleSectionChange}
              onClassesLoaded={setClasses}
              showingAllSections={showAllSections}
              onShowAllSections={() => setShowAllSections(true)}
            />

            {sectionRequired && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  background: '#fffbeb',
                  border: '1px solid #fcd34d',
                  borderRadius: 12,
                  padding: '10px 16px',
                  marginBottom: 16,
                  fontSize: 13,
                  color: '#92400e',
                  fontWeight: 600,
                }}
              >
                <i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b' }} />
                Please select a section (or &quot;All Sections&quot;) before uploading.
              </div>
            )}

            <div
              ref={dropRef}
              className={`bpp-drop-zone${isDragging ? ' dragging' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed #c7d2fe',
                borderRadius: 20,
                padding: '40px 24px',
                marginBottom: 24,
                cursor: 'pointer',
                textAlign: 'center',
                background: isDragging ? 'rgba(65,105,225,0.06)' : 'white',
                boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.gif"
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 16,
                  margin: '0 auto 16px',
                  background:
                    'linear-gradient(135deg, rgba(65,105,225,0.1), rgba(91,124,230,0.08))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid rgba(65,105,225,0.15)',
                }}
              >
                <i className="pi pi-cloud-upload" style={{ fontSize: 26, color: '#4169E1' }} />
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: '#1f2937',
                  marginBottom: 6,
                }}
              >
                {isDragging ? 'Drop files here' : 'Drag & drop images here'}
              </div>
              <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 14 }}>
                or click to browse — JPG, PNG, GIF · max 500 KB each
              </div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 20px',
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #4169E1, #5B7CE6)',
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(65,105,225,0.3)',
                }}
              >
                <i className="pi pi-folder-open" style={{ fontSize: 13 }} />
                Choose Files
              </div>
            </div>

            {summary && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 10,
                  background:
                    summary.failed === 0
                      ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
                      : summary.success === 0
                        ? 'linear-gradient(135deg, #fef2f2, #fee2e2)'
                        : 'linear-gradient(135deg, #fffbeb, #fef9c3)',
                  border: `1px solid ${
                    summary.failed === 0
                      ? '#bbf7d0'
                      : summary.success === 0
                        ? '#fecaca'
                        : '#fde68a'
                  }`,
                  borderRadius: 14,
                  padding: '12px 20px',
                  marginBottom: 20,
                  animation: 'fadeSlideIn 0.3s ease-out',
                }}
              >
                <div style={{ display: 'flex', gap: 20 }}>
                  {[
                    { label: 'Total', value: summary.total, color: '#6b7280' },
                    { label: 'Success', value: summary.success, color: '#16a34a' },
                    { label: 'Failed', value: summary.failed, color: '#dc2626' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
                {summary.failed > 0 && (
                  <button
                    onClick={retryFailed}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '7px 16px',
                      borderRadius: 10,
                      border: 'none',
                      background: '#fef2f2',
                      color: '#dc2626',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    <i className="pi pi-refresh" style={{ fontSize: 12 }} />
                    Retry Failed ({summary.failed})
                  </button>
                )}
              </div>
            )}

            {entries.length > 0 && (
              <div
                style={{
                  background: 'white',
                  borderRadius: 20,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 20px',
                    background: 'linear-gradient(135deg, #ffffff, #fafbfc)',
                    borderBottom: '1px solid #f3f4f6',
                    flexWrap: 'wrap',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background:
                          'linear-gradient(135deg, rgba(65,105,225,0.1), rgba(91,124,230,0.08))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className="pi pi-images" style={{ fontSize: 14, color: '#4169E1' }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>
                      {entries.length} file{entries.length !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 12, fontWeight: 600 }}>
                    {pendingEntries.length > 0 && (
                      <span style={{ color: '#6b7280' }}>
                        {pendingEntries.length} pending
                      </span>
                    )}
                    {errorEntries.length > 0 && (
                      <span style={{ color: '#dc2626' }}>
                        {errorEntries.length} invalid
                      </span>
                    )}
                    {successEntries.length > 0 && (
                      <span style={{ color: '#16a34a' }}>
                        {successEntries.length} done
                      </span>
                    )}
                  </div>
                </div>

                <div
                  className="bpp-file-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '56px 1fr 100px 160px 36px',
                    gap: 12,
                    padding: '10px 20px',
                    background: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                  }}
                >
                  <span>Preview</span>
                  <span>Filename / Roll No</span>
                  <span className="bpp-file-col-header-size">Size</span>
                  <span className="bpp-file-col-header-status">Status</span>
                  <span />
                </div>

                {entries.map((entry, idx) => (
                  <div
                    key={entry.id}
                    className="bpp-entry-row bpp-file-grid"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '56px 1fr 100px 160px 36px',
                      gap: 12,
                      padding: '12px 20px',
                      alignItems: 'center',
                      borderBottom:
                        idx < entries.length - 1 ? '1px solid #f3f4f6' : 'none',
                      background:
                        entry.status === 'error'
                          ? 'rgba(239,68,68,0.02)'
                          : entry.status === 'success'
                            ? 'rgba(22,163,74,0.02)'
                            : 'white',
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 10,
                        overflow: 'hidden',
                        border: `2px solid ${
                          entry.status === 'error'
                            ? '#fecaca'
                            : entry.status === 'success'
                              ? '#bbf7d0'
                              : '#e5e7eb'
                        }`,
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={entry.preview}
                        alt={entry.rollNumber}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 13,
                          color: '#1f2937',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {entry.file.name}
                      </div>
                      {entry.status === 'error' && entry.errorMsg && (
                        <div
                          style={{
                            fontSize: 11,
                            color: '#dc2626',
                            marginTop: 2,
                            fontWeight: 500,
                          }}
                        >
                          <i
                            className="pi pi-exclamation-circle"
                            style={{ fontSize: 10, marginRight: 4 }}
                          />
                          {entry.errorMsg}
                        </div>
                      )}
                      {entry.status === 'success' && (
                        <div
                          style={{
                            fontSize: 11,
                            color: '#16a34a',
                            marginTop: 2,
                            fontWeight: 500,
                          }}
                        >
                          <i className="pi pi-check" style={{ fontSize: 10, marginRight: 4 }} />
                          Uploaded successfully
                        </div>
                      )}
                      {entry.status === 'pending' && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                          Roll No:{' '}
                          <strong style={{ color: '#6b7280' }}>{entry.rollNumber}</strong>
                        </div>
                      )}
                    </div>

                    <div
                      className="bpp-file-size-col"
                      style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}
                    >
                      {(entry.file.size / 1024).toFixed(0)} KB
                    </div>

                    <div className="bpp-file-status-col">
                      <StatusBadge status={entry.status} />
                    </div>

                    <button
                      className="bpp-remove-btn"
                      onClick={() => removeEntry(entry.id)}
                      disabled={entry.status === 'uploading'}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                        background: '#f9fafb',
                        cursor:
                          entry.status === 'uploading' ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9ca3af',
                      }}
                    >
                      <i className="pi pi-times" style={{ fontSize: 11 }} />
                    </button>
                  </div>
                ))}

                {pendingEntries.length > 0 && (
                  <div
                    style={{
                      padding: '16px 20px',
                      borderTop: '1px solid #f3f4f6',
                      background: 'linear-gradient(135deg, #fafbfc, #ffffff)',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 10,
                    }}
                  >
                    {errorEntries.length > 0 &&
                      entries.some(
                        (e) => e.status === 'error' && /^\d+$/.test(e.rollNumber)
                      ) && (
                        <button
                          onClick={retryFailed}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '9px 18px',
                            borderRadius: 10,
                            border: '1.5px solid #c7d2fe',
                            background: 'white',
                            color: '#4169E1',
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          <i className="pi pi-refresh" style={{ fontSize: 12 }} />
                          Retry Invalid
                        </button>
                      )}
                    <button
                      onClick={handleUpload}
                      disabled={!canUpload || sectionRequired}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '9px 22px',
                        borderRadius: 10,
                        border: 'none',
                        background:
                          canUpload && !sectionRequired
                            ? 'linear-gradient(135deg, #4169E1, #5B7CE6)'
                            : '#e5e7eb',
                        color:
                          canUpload && !sectionRequired ? 'white' : '#9ca3af',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor:
                          canUpload && !sectionRequired ? 'pointer' : 'not-allowed',
                        boxShadow:
                          canUpload && !sectionRequired
                            ? '0 4px 14px rgba(65,105,225,0.3)'
                            : 'none',
                      }}
                    >
                      <i
                        className={`pi ${
                          uploading ? 'pi-spin pi-spinner' : 'pi-cloud-upload'
                        }`}
                        style={{ fontSize: 13 }}
                      />
                      {uploading
                        ? 'Uploading…'
                        : `Upload ${pendingEntries.length} Photo${
                            pendingEntries.length !== 1 ? 's' : ''
                          }`}
                    </button>
                  </div>
                )}
              </div>
            )}

            {entries.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '20px 20px 40px',
                  color: '#9ca3af',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  No files added yet — drag images above or click &quot;Choose Files&quot;
                </div>
              </div>
            )}
          </>
        )}

        {/* ══════════════════ PHOTO STATUS TAB ══════════════════ */}
        {activeTab === 'status' && (
          <div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 20,
              }}
            >
              {classes.map((cls) => (
                <button
                  key={cls.id}
                  onClick={() => fetchPhotoStatus(cls.id)}
                  disabled={statusLoading || !sessionId}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 16px',
                    borderRadius: 10,
                    border: 'none',
                    cursor: statusLoading || !sessionId ? 'not-allowed' : 'pointer',
                    fontWeight: 700,
                    fontSize: 12,
                    background:
                      statusClassId === cls.id
                        ? 'linear-gradient(135deg, #4169E1, #5B7CE6)'
                        : 'white',
                    color: statusClassId === cls.id ? 'white' : '#6b7280',
                    boxShadow:
                      statusClassId === cls.id
                        ? '0 3px 10px rgba(65,105,225,0.3)'
                        : '0 1px 4px rgba(0,0,0,0.08)',
                    opacity: !sessionId ? 0.5 : 1,
                  }}
                >
                  <i className="pi pi-users" style={{ fontSize: 11 }} />
                  {cls.class_name}
                </button>
              ))}

              <button
                onClick={() => statusClassId && fetchPhotoStatus(statusClassId)}
                disabled={statusLoading || !statusClassId || !sessionId}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: '1.5px solid #e5e7eb',
                  background: 'white',
                  color: '#6b7280',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginLeft: 'auto',
                }}
              >
                <i
                  className={`pi ${statusLoading ? 'pi-spin pi-spinner' : 'pi-refresh'}`}
                  style={{ fontSize: 11 }}
                />
                {statusLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            {statusLoading && !photoStatus && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#9ca3af',
                }}
              >
                <i
                  className="pi pi-spin pi-spinner"
                  style={{ fontSize: 28, marginBottom: 12 }}
                />
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  Loading photo status…
                </div>
              </div>
            )}

            {photoStatus && !statusLoading && (
              <>
                {statusClassId && (
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#1f2937',
                      marginBottom: 12,
                    }}
                  >
                    {getClassName(statusClassId)}
                  </div>
                )}

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                    gap: 12,
                    marginBottom: 20,
                  }}
                >
                  {[
                    {
                      label: 'Total Students',
                      value: photoStatus.grand_total,
                      color: '#6b7280',
                      icon: 'pi-users',
                    },
                    {
                      label: 'Have Photo',
                      value: photoStatus.grand_with_photo,
                      color: '#16a34a',
                      icon: 'pi-check-circle',
                    },
                    {
                      label: 'Missing Photo',
                      value: photoStatus.grand_without_photo,
                      color: '#dc2626',
                      icon: 'pi-times-circle',
                    },
                    {
                      label: 'Coverage',
                      value:
                        photoStatus.grand_total > 0
                          ? `${Math.round(
                              (photoStatus.grand_with_photo /
                                photoStatus.grand_total) *
                                100
                            )}%`
                          : '0%',
                      color: '#4169E1',
                      icon: 'pi-chart-pie',
                    },
                  ].map(({ label, value, color, icon }) => (
                    <div
                      key={label}
                      style={{
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: 14,
                        padding: '16px 18px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className={`pi ${icon}`} style={{ fontSize: 13, color }} />
                        <span
                          style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}
                        >
                          {label}
                        </span>
                      </div>
                      <div style={{ fontSize: 26, fontWeight: 800, color }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    background: 'white',
                    borderRadius: 20,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    className="status-table-header"
                    style={{
                      gap: 12,
                      padding: '10px 20px',
                      background: '#f9fafb',
                      borderBottom: '1px solid #e5e7eb',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                    }}
                  >
                    <span>Section</span>
                    <span>Total</span>
                    <span style={{ color: '#16a34a' }}>With Photo</span>
                    <span style={{ color: '#dc2626' }}>Missing</span>
                    <span>Coverage</span>
                    <span />
                  </div>

                  {(photoStatus.sections ?? []).map((sec, si) => {
                    const secKey = String(sec.section ?? 'none');
                    const secExpanded = expandedSections.has(secKey);
                    const secCoverage =
                      sec.total_students > 0
                        ? Math.round(
                            (sec.with_photo_count / sec.total_students) * 100
                          )
                        : 0;
                    const sectionLabel =
                      !sec.section || sec.section === 'NO_SECTION'
                        ? 'No Section'
                        : sec.section;

                    return (
                      <div
                        key={secKey}
                        style={{
                          borderBottom:
                            si < photoStatus.sections.length - 1
                              ? '1px solid #f3f4f6'
                              : 'none',
                        }}
                      >
                        <div
                          className="status-section-row status-section-grid"
                          onClick={() => toggleSectionExpand(secKey)}
                          style={{
                            gap: 12,
                            padding: '14px 20px',
                            alignItems: 'center',
                            background: secExpanded
                              ? 'rgba(65,105,225,0.03)'
                              : 'white',
                          }}
                        >
                          <div
                            className="status-section-title"
                            style={{ display: 'flex', flexDirection: 'column' }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              <i
                                className="pi pi-users"
                                style={{ fontSize: 12, color: '#4169E1' }}
                              />
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: '#1f2937',
                                }}
                              >
                                {sectionLabel}
                              </span>
                            </div>
                            <div className="status-section-mobile-stats">
                              <span
                                className="mobile-stat-pill"
                                style={{ background: '#f3f4f6', color: '#6b7280' }}
                              >
                                <i className="pi pi-users" style={{ fontSize: 9 }} />{' '}
                                {sec.total_students}
                              </span>
                              <span
                                className="mobile-stat-pill"
                                style={{ background: '#f0fdf4', color: '#16a34a' }}
                              >
                                <i className="pi pi-check" style={{ fontSize: 9 }} />{' '}
                                {sec.with_photo_count}
                              </span>
                              {sec.without_photo_count > 0 && (
                                <span
                                  className="mobile-stat-pill"
                                  style={{
                                    background: '#fef2f2',
                                    color: '#dc2626',
                                  }}
                                >
                                  <i className="pi pi-times" style={{ fontSize: 9 }} />{' '}
                                  {sec.without_photo_count}
                                </span>
                              )}
                              <span
                                className="mobile-stat-pill"
                                style={{
                                  background:
                                    secCoverage >= 80
                                      ? '#f0fdf4'
                                      : secCoverage >= 50
                                        ? '#fffbeb'
                                        : '#fef2f2',
                                  color:
                                    secCoverage >= 80
                                      ? '#16a34a'
                                      : secCoverage >= 50
                                        ? '#d97706'
                                        : '#dc2626',
                                }}
                              >
                                {secCoverage}%
                              </span>
                              <i
                                className={`pi ${
                                  secExpanded ? 'pi-chevron-up' : 'pi-chevron-down'
                                }`}
                                style={{
                                  fontSize: 10,
                                  color: '#9ca3af',
                                  marginLeft: 2,
                                }}
                              />
                            </div>
                          </div>

                          <span
                            className="scol-total"
                            style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}
                          >
                            {sec.total_students}
                          </span>
                          <span
                            className="scol-with"
                            style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}
                          >
                            {sec.with_photo_count}
                          </span>
                          <span
                            className="scol-missing"
                            style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}
                          >
                            {sec.without_photo_count}
                          </span>
                          <div
                            className="scol-coverage"
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                          >
                            <div
                              style={{
                                flex: 1,
                                height: 6,
                                background: '#f3f4f6',
                                borderRadius: 3,
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  height: '100%',
                                  borderRadius: 3,
                                  width: `${secCoverage}%`,
                                  background:
                                    secCoverage >= 80
                                      ? '#16a34a'
                                      : secCoverage >= 50
                                        ? '#f59e0b'
                                        : '#dc2626',
                                }}
                              />
                            </div>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#6b7280',
                                minWidth: 32,
                              }}
                            >
                              {secCoverage}%
                            </span>
                          </div>
                          <i
                            className={`scol-chevron pi ${
                              secExpanded ? 'pi-chevron-up' : 'pi-chevron-down'
                            }`}
                            style={{ fontSize: 11, color: '#9ca3af' }}
                          />
                        </div>

                        {secExpanded && (
                          <div
                            style={{
                              padding: '0 16px 12px 16px',
                              display: 'grid',
                              gridTemplateColumns:
                                'repeat(auto-fit, minmax(240px, 1fr))',
                              gap: 12,
                            }}
                          >
                            {sec.without_photo.length > 0 && (
                              <div
                                style={{
                                  background: '#fef2f2',
                                  border: '1px solid #fecaca',
                                  borderRadius: 10,
                                  padding: '10px 12px',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#dc2626',
                                    marginBottom: 6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                  }}
                                >
                                  <i
                                    className="pi pi-times-circle"
                                    style={{ fontSize: 10 }}
                                  />
                                  Missing Photo ({sec.without_photo.length})
                                </div>
                                {sec.without_photo.map((s) => (
                                  <div
                                    key={s.user_id}
                                    style={{
                                      fontSize: 12,
                                      color: '#7f1d1d',
                                      padding: '3px 0',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      borderBottom:
                                        '1px solid rgba(254,202,202,0.5)',
                                    }}
                                  >
                                    <span style={{ fontWeight: 500 }}>{s.name}</span>
                                    <span style={{ color: '#9ca3af', fontSize: 11 }}>
                                      Roll {s.roll_no}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {sec.with_photo.length > 0 && (
                              <div
                                style={{
                                  background: '#f0fdf4',
                                  border: '1px solid #bbf7d0',
                                  borderRadius: 10,
                                  padding: '10px 12px',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#16a34a',
                                    marginBottom: 6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                  }}
                                >
                                  <i
                                    className="pi pi-check-circle"
                                    style={{ fontSize: 10 }}
                                  />
                                  Has Photo ({sec.with_photo.length})
                                </div>
                                {sec.with_photo.map((s) => (
                                  <div
                                    key={s.user_id}
                                    style={{
                                      fontSize: 12,
                                      color: '#14532d',
                                      padding: '4px 0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 8,
                                      borderBottom:
                                        '1px solid rgba(187,247,208,0.5)',
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        minWidth: 0,
                                        flex: 1,
                                      }}
                                    >
                                      {s.profile_picture_url && (
                                        <img
                                          src={s.profile_picture_url}
                                          alt=""
                                          style={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: 6,
                                            objectFit: 'cover',
                                            flexShrink: 0,
                                          }}
                                        />
                                      )}
                                      <span
                                        style={{
                                          fontWeight: 500,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {s.name}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <span
                                        style={{ color: '#9ca3af', fontSize: 11 }}
                                      >
                                        Roll {s.roll_no}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeletePhoto(s.user_id, s.name);
                                        }}
                                        disabled={deletingUserId === s.user_id}
                                        title="Delete photo"
                                        style={{
                                          border: 'none',
                                          background: '#fef2f2',
                                          color: '#dc2626',
                                          borderRadius: 6,
                                          padding: '2px 6px',
                                          cursor:
                                            deletingUserId === s.user_id
                                              ? 'wait'
                                              : 'pointer',
                                          fontSize: 10,
                                        }}
                                      >
                                        <i
                                          className={`pi ${
                                            deletingUserId === s.user_id
                                              ? 'pi-spin pi-spinner'
                                              : 'pi-trash'
                                          }`}
                                          style={{ fontSize: 10 }}
                                        />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {!photoStatus && !statusLoading && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#9ca3af',
                }}
              >
                <i
                  className="pi pi-chart-bar"
                  style={{ fontSize: 32, marginBottom: 12 }}
                />
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {!sessionId
                    ? 'Active session not loaded — check school details store'
                    : 'Click a class button above to load photo status'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: FileEntry['status'] }> = ({ status }) => {
  const cfg = {
    pending: {
      bg: '#f3f4f6',
      color: '#6b7280',
      icon: 'pi-clock',
      label: 'Pending',
    },
    uploading: {
      bg: '#eff6ff',
      color: '#3b82f6',
      icon: 'pi-spin pi-spinner',
      label: 'Uploading',
    },
    success: {
      bg: '#f0fdf4',
      color: '#16a34a',
      icon: 'pi-check-circle',
      label: 'Uploaded',
    },
    error: {
      bg: '#fef2f2',
      color: '#dc2626',
      icon: 'pi-times-circle',
      label: 'Failed',
    },
  }[status];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 11px',
        borderRadius: 20,
        background: cfg.bg,
        color: cfg.color,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      <i className={`pi ${cfg.icon}`} style={{ fontSize: 11 }} />
      {cfg.label}
    </span>
  );
};

export default BulkProfilePictureUpload;